import ContentLoader from 'react-content-loader';
import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { Integer, DecimalRounded } from '../../../utils/NumeralParts';
import Formatters from '../../../features/shared/utils/Formatters';
import LoanTermsLabelConverter from '../../../utils/LoanTermsLabelConverter';

export default class LoanOfferPreview extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isBreakdownVisible: false,
    };
  }

  static defaultProps = {
    loading: true,
  }

  static propTypes = {
    loading: PropTypes.bool,
    loanOffer: PropTypes.shape({
      custom_fields: PropTypes.shape({
        disbursed_amount: PropTypes.number,
        loan_amount: PropTypes.number,
        service_fee_amount: PropTypes.number,
        service_fee: PropTypes.number,
        term_length: PropTypes.number,
      })
    }),
    selectedOption: PropTypes.shape({
      frequency: PropTypes.number,
      term_length: PropTypes.number,
      principal: PropTypes.number,
      interest: PropTypes.number,
      fee: PropTypes.number,
      total: PropTypes.number,
      principal_amount: PropTypes.number,
      start_date: PropTypes.string,
      interest_rate: PropTypes.string,
      frequency_mode: PropTypes.string,
      repayment_date: PropTypes.string,
      payment_schedules: PropTypes.arrayOf(PropTypes.shape({
        due_date: PropTypes.string,
        beginning_balance: PropTypes.number,
        principal: PropTypes.number,
        interest: PropTypes.number,
        fee: PropTypes.number,
        total: PropTypes.number,
        ending_balance: PropTypes.number,
      }))
    }),
    fcPayment: PropTypes.shape({
      id: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired,
      payment_date: PropTypes.string,
      supplier: PropTypes.shape({
        bank: PropTypes.shape({
          bank_name: PropTypes.string,
        }),
        business_name: PropTypes.string,
        bank_account_number: PropTypes.string,
      })
    })
  }

  get hasSubscriptionFee() {
    const {
      loanOffer: {
        custom_fields: {
          service_fee_amount: serviceFeeAmount,
        } = {},
      },
    } = this.props;

    return serviceFeeAmount === 0;
  }

  get convenienceFeeAmount() {
    const {
      loanOffer: {
        custom_fields: {
          service_fee_amount: serviceFeeAmount,
        } = {},
      },
      selectedOption,
    } = this.props;
    const calculatedServiceFeeAmount =
      this.hasSubscriptionFee
        ? selectedOption.principal_amount * (1.99 / 100)
        : serviceFeeAmount;

    return calculatedServiceFeeAmount;
  }

  get disbursedAmount() {
    const {
      loanOffer: {
        custom_fields: {
          disbursed_amount: disbursedAmount
        } = {},
      },
      selectedOption: option,
    } = this.props;
    const { principal_amount } = option;
    const disbursed_amount =
      disbursedAmount === 0 ? principal_amount : disbursedAmount;
    return Math.round(disbursed_amount);
  }

  get interestAmount() {
    const { selectedOption } = this.props;
    const convenienceFeeAmount = this.hasSubscriptionFee ? 0.0 : this.convenienceFeeAmount;
    const interest = selectedOption.total - this.disbursedAmount - convenienceFeeAmount;

    return Formatters.currencyDecimal(interest);
  }

  onClickBreakdown = (e) => {
    e.preventDefault();
    this.setState({
      isBreakdownVisible: !this.state.isBreakdownVisible,
    });
  };

  onClickNext = (e) => {
    e.preventDefault();
    this.props.onClickNext();
  };

  renderFrequency = () => {
    const { selectedOption: option } = this.props;
    const selectedOptionTermsLabel = LoanTermsLabelConverter({
      repaymentFrequency: option.frequency,
      repaymentFrequencyMode: option.frequency_mode
    });

    return selectedOptionTermsLabel;
  };

  renderDuration = () => {
    let { selectedOption: option, loanOffer } = this.props;
    let term = option.term_mode || 'month';
    let term_length =
      option.term_length === 0
        ? loanOffer.custom_fields.term_length
        : option.term_length;
    if (term_length > 1) {
      term = `${term}s`;
    }
    return `${term_length} ${term}`;
  };

  renderPaymentCount = () => {
    const { selectedOption } = this.props;
    let copy = 'payment';
    const paymentsCount = selectedOption.payment_schedules.length;
    if (this.isBullet()) copy = `${copy}s`;
    return `${paymentsCount} ${copy}`;
  };

  isBullet = () => {
    const { selectedOption } = this.props;
    return selectedOption.payment_schedules.length > 1
  }

  renderPaymentDetails = () => {
    const {
      payment_date,
      supplier: {
        bank_account_number,
        business_name,
        bank: { bank_name }
      }
    } = this.props.fcPayment;

    return (
        <section
          className='cardSection _paddingBottom--none'
        >
          <p className='_textAlign_center subhead -primary _marginBottom--normal'>Payment will be sent to</p>
          <h2 className='subhead _fontSize--large -darkBlue _displayFlex _alignItems-center _marginLeft--normal _marginRight--normal _marginBottom--large'>
            { business_name }
          </h2>
          <div className='_marginLeft--normal _marginRight--normal'>
            <div className='_displayFlex _justifyContent-space-between _alignItems-center _paddingBottom--small'>
              <p className='subhead _marginBottom--none _textAlign_left _fontSize--normal-mobileOnly _marginRight--small-mobileOnly'>
                Payment Date
              </p>
              <p
                className='_fontSize--normal-mobileOnly _whiteSpace_nowrap'
              >
                { Formatters.date(payment_date) }
              </p>
            </div>
            <div className='_displayFlex _justifyContent-space-between _alignItems-center _paddingBottom--small'>
              <p className='subhead _marginBottom--none _textAlign_left _fontSize--normal-mobileOnly _marginRight--small-mobileOnly'>
                Bank Name
              </p>
              <p
                className='_fontSize--normal-mobileOnly _whiteSpace_nowrap'
              >
                { bank_name }
              </p>
            </div>
            <div className='_displayFlex _justifyContent-space-between _alignItems-center _paddingBottom--large'>
              <p className='subhead _marginBottom--none _textAlign_left _fontSize--normal-mobileOnly _marginRight--small-mobileOnly'>
                Bank Account No.
              </p>
              <p
                className='_fontSize--normal-mobileOnly _whiteSpace_nowrap'
              >
                { bank_account_number }
              </p>
            </div>
          </div>
        </section>
    )
  }

  renderPreview = () => {
    const { selectedOption } = this.props;
    const firstSchedule = selectedOption.payment_schedules[0];

    return (
      <div
        data-testid='loan-offer-preview-container'
        style={{ height: '408px' }}
      >
        <section
          className='cardSection _paddingBottom--none _borderBottom_unset'
        >
          <p className='_textAlign_center subhead -primary'>You will pay</p>
          <div className='_displayFlex _justifyContent-center _alignItems-center'>
            <span className='-primary _marginRight--large _marginBottom--small emphasize'>
              ₱
            </span>
            <h2
              className='headline -primary _fontSize--xxlarge'
              data-testid='loan-offer-preview-payment-per-frequency-whole'
            >
              {Integer(firstSchedule.total)}
            </h2>
            <span
              className='-primary _alignSelf-center emphasize'
              data-testid='loan-offer-preview-payment-per-frequency-decimal'
            >
              {`${DecimalRounded({ number: firstSchedule.total })}`}
            </span>
          </div>
          <p
            className='subhead _textAlign_center _marginTop--none _marginBottom--small'
            data-testid='loan-offer-preview-frequency'
          >
            {this.renderFrequency()}
          </p>
          <div className='_marginLeft--normal _marginRight--normal'>
            <div className='_displayFlex _justifyContent-space-between _paddingTop--small'>
              <p className='subhead _textAlign_left _fontSize--normal-mobileOnly _marginRight--small-mobileOnly'>
                Interest Rate
              </p>
              <p
                className='_fontSize--normal-mobileOnly _whiteSpace_nowrap'
                data-testid='loan-offer-preview-interest-rate'
              >
                {this.renderInterest()}
              </p>
            </div>
            <div className='_displayFlex _justifyContent-space-between'>
              <p className='subhead _textAlign_left _fontSize--normal-mobileOnly _marginRight--small-mobileOnly'>
                Loan Duration
              </p>
              <p
                className='_fontSize--normal-mobileOnly _whiteSpace_nowrap'
                data-testid='loan-offer-preview-duration'
              >
                {this.renderDuration()}
              </p>
            </div>
            <div className='_displayFlex _justifyContent-space-between'>
              <p className='subhead _textAlign_left _fontSize--normal-mobileOnly _marginRight--small-mobileOnly'>
                Number of Payments
              </p>
              <p
                className='_fontSize--normal-mobileOnly _whiteSpace_nowrap'
                data-testid='loan-offer-preview-payment-count'
              >
                {this.renderPaymentCount()}
              </p>
            </div>
            <div className='_displayFlex _justifyContent-space-between'>
              <p className='subhead _textAlign_left _fontSize--normal-mobileOnly _marginRight--small-mobileOnly'>
                Total You Will Pay
              </p>
              <p
                className='_fontSize--normal-mobileOnly _whiteSpace_nowrap'
              >
                <span data-testid='loan-offer-preview-total-payable-amount'>
                  ₱ {Formatters.currencyDecimal(selectedOption.total)}
                </span>
              </p>
              <i
                style={{
                  position: 'absolute',
                  right: 0,
                  marginTop: '-4px',
                  marginRight: '12px',
                }}
                data-testid='toggle-breakdown-button'
                className='materialIcon -primary --small _marginLeft--xxsmall'
                onClick={this.onClickBreakdown}
              >
                {this.state.isBreakdownVisible
                  ? 'keyboard_arrow_up'
                  : 'keyboard_arrow_down'}
              </i>
            </div>
          </div>
        </section>

        {this.renderBreakdown()}
      </div>
    );
  };

  renderInterest(){
    const { selectedOption } = this.props;
    return `${Formatters.currencyDecimal(selectedOption.equivalent_aor, false)}%/mo.`;
  }

  renderBreakdown = () => {
    const {
      selectedOption,
    } = this.props;
    const style = {
      visibility: this.state.isBreakdownVisible ? 'visible' : 'hidden',
    };

    return (
      <div
        className='_backgroundColor-sh050 _padding--large _paddingTop--xxsmall'
        style={style}
      >
        <div className='_marginLeft--normal _marginRight--normal'>
          <div className='_displayFlex _justifyContent-space-between _paddingTop--small'>
            <p className='-darkBlue _textAlign_left _fontSize--normal-mobileOnly _marginRight--small-mobileOnly'>
              Amount { this.isForPayment ? 'Supplier' : 'You'} Will Receive
            </p>
            <p
              className='-darkBlue _fontSize--normal-mobileOnly _whiteSpace_nowrap'
              data-testid='loan-offer-preview-breakdown-disbursed-amount'
            >
              {this.renderDisburseAmount()}
            </p>
          </div>
          <div className='_displayFlex _justifyContent-space-between _paddingTop--small'>
            <p className='-secondary _textAlign_left _fontSize--normal-mobileOnly _marginRight--small-mobileOnly'>
              + Convenience Fee
            </p>
            {this.renderConvenienceFee()}
          </div>
          <div className='_displayFlex _justifyContent-space-between _paddingTop--small'>
            <p className='-secondary _textAlign_left _fontSize--normal-mobileOnly _marginRight--small-mobileOnly'>
              + Interest (
              {`${Formatters.currencyDecimal(
                selectedOption.equivalent_aor
              )}%/mo`}
              )
            </p>
            <p
              className='_fontSize--normal-mobileOnly -secondary _whiteSpace_nowrap'
              data-testid='loan-offer-preview-breakdown-interest-amount'
            >
              + {this.interestAmount}
            </p>
          </div>
          <div
            className='_marginTop--small'
            style={{ height: '1px', backgroundColor: '#CDCEE7' }}
          ></div>
          <div className='_displayFlex _justifyContent-space-between _paddingTop--small'>
            <p className='-darkBlue _textAlign_left _fontSize--normal-mobileOnly _marginRight--small-mobileOnly'>
              Total You Will Pay
            </p>
            <p
              className='-darkBlue _fontSize--normal-mobileOnly _whiteSpace_nowrap'
              data-testid='loan-offer-preview-breakdown-total-payable-amount'
            >
              = ₱ {Formatters.currencyDecimal(selectedOption.total)}
            </p>
          </div>
        </div>
      </div>
    );
  };

  renderDisburseAmount = () => {
    return `₱ ${Formatters.currencyDecimal(this.disbursedAmount)}`
  }

  renderConvenienceFee = () => {
    let labelStyle = { textDecoration: 'line-through' };
    const calculatedConvenienceFeeAmount = this.convenienceFeeAmount;

    return (
      <p
        className='_fontSize--normal-mobileOnly -secondary _whiteSpace_nowrap'
        data-testid='loan-offer-preview-breakdown-convenience-fee-amount'
      >
        <span
          data-testid='loan-offer-preview-breakdown-convenience-fee-value'
          style={this.hasSubscriptionFee ? labelStyle : {}}
        >
          + {Formatters.currencyDecimal(calculatedConvenienceFeeAmount)}
        </span>
        {this.hasSubscriptionFee && <span> 0.00</span>}
      </p>
    );
  };
  renderLoading = () => {
    return (
      <div data-testid='loan-offer-preview-loading' style={{ height: '408px' }}>
        <section
          className='cardSection _paddingBottom--none _borderBottom_unset'
        >
          <p className='_textAlign_center subhead -primary'>You will pay</p>
        </section>
        <ContentLoader
          height={300}
          width={384}
          speed={2}
          primaryColor='#f3f3f3'
          secondaryColor='#b2c9e0'
        >
          <rect x='85' y='12' rx='0' ry='0' width='214' height='36' />
          <rect x='120' y='60' rx='0' ry='0' width='144' height='20' />

          <rect x='38' y='100' rx='0' ry='0' width='156' height='20' />
          <rect x='242' y='100' rx='0' ry='0' width='104' height='20' />
          <rect x='38' y='130' rx='0' ry='0' width='156' height='20' />
          <rect x='242' y='130' rx='0' ry='0' width='104' height='20' />
          <rect x='38' y='160' rx='0' ry='0' width='156' height='20' />
          <rect x='242' y='160' rx='0' ry='0' width='104' height='20' />
          <rect x='38' y='190' rx='0' ry='0' width='156' height='20' />
          <rect x='226' y='190' rx='0' ry='0' width='120' height='20' />
        </ContentLoader>
      </div>
    );
  };

  get isLoading(){
    const { loading, selectedOption} = this.props;
    return !selectedOption || (loading && selectedOption)
  }

  get label(){
    if(this.isLoading || this.props.loadingBtn) return "Loading..."
    return "Next"
  }

  get isForPayment() {
    return !isEmpty(this.props.fcPayment);
  }

  get heading() {
    if (this.isForPayment) { return 'Your Payment Details' }

    return 'Your Loan Details'
  }

  render() {
    return (
      <div className='ds-card'>
        <div className='cardSection'>
          <p
            className='subhead -primary _fontSize--large _textAlign_center'
            data-testid='loan-offer-header-title'
          >
            { this.heading }
          </p>
        </div>
        {this.isForPayment && this.renderPaymentDetails()}
        {!this.isLoading && this.renderPreview()}
        {this.isLoading && this.renderLoading()}
        <button
          data-testid='loan-offer-preview-next-button'
          disabled={this.isLoading || this.props.loadingBtn }
          onClick={this.onClickNext}
          className='ds-btn --full'
        >
          {this.label}
        </button>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/features/loans/components/LoanOfferPreview.js